<template>
  <div class="list-demo-cars" v-if="canView">
    <KTCodePreview v-bind:title="'Danh sách xe Demo'">
      <!-- Toolbar Section -->
      <template v-slot:toolbar>
        <b-row>
          <b-dropdown size="sm" right>
            <template slot="button-content">
              <i style="font-size: 1rem" class="fas fa-cog"></i>
              <span class="font-weight-bolder">Thao tác</span>
            </template>
            <b-dropdown-item @click="handleExportExcel" v-if="canExport">
              <span>
                <i style="font-size: 1rem" class="far fa-file-excel"></i>
                &nbsp; Xuất Excel</span
              >
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            class="font-weight-bolder ml-3"
            @click="createNewDemoCar"
            size="sm"
            variant="primary"
            v-if="canInsert"
          >
            <i style="font-size: 1rem" class="flaticon2-add-1"></i>Thêm mới
          </b-button>
        </b-row>
      </template>

      <!-- Main Content Section -->
      <template v-slot:preview>
        <!-- Filter Section -->
        <b-row class="mb-5">
          <div class="col d-flex align-items-center" style="column-gap: 10px">
            <date-picker
              placeholder="Chọn ngày bắt đầu"
              class="form-control form-control-sm w-100"
              v-model="filters.fromDate"
              :config="configs.date"
            >
            </date-picker>

            <date-picker
              placeholder="Chọn ngày kết thúc"
              class="form-control form-control-sm w-100"
              v-model="filters.toDate"
              :config="configs.date"
            >
            </date-picker>
          </div>

          <div class="col">
            <b-input
              size="sm"
              placeholder="Tên sản phẩm"
              v-model="filters.productName"
              append-icon="search"
              single-line
              hide-details
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </div>

          <div class="col">
            <b-input
              size="sm"
              placeholder="Số khung"
              v-model="filters.chassisNumber"
              append-icon="search"
              single-line
              hide-details
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </div>

          <div class="col">
            <b-input
              size="sm"
              placeholder="Biển số"
              v-model="filters.licensePlate"
              append-icon="search"
              single-line
              hide-details
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </div>

          <div class="col">
            <b-form-select
              size="sm"
              required
              class="select-style"
              v-model="filters.status"
              :options="statusOptions"
              value-field="id"
              text-field="name"
              disabled-field="notEnabled"
            ></b-form-select>
          </div>
        </b-row>

        <b-row class="mb-5">
          <b-col md="1">
            <b-button
              style="font-weight: 600; width: 70px"
              variant="primary"
              size="sm"
              @click="onFilter"
              >Lọc</b-button
            >
          </b-col>
          <b-col md="1" class="pl-0">
            <b-button
              style="font-weight: 600; width: 70px"
              size="sm"
              class="ml-2"
              v-b-tooltip.hover
              title="Reset dữ liệu"
              @click="resetFilters"
              variant="secondary"
            >
              <i style="padding-right: 0" class="fa fa-redo"></i>
            </b-button>
          </b-col>
          <b-col>
            <div class="d-flex justify-content-end">
              <p
                class="mt-3 text-dark"
                style="font-weight: 500; font-size: 13px"
              >
                {{ fromNo }} - {{ toNo }} /
                {{ convertPrice(totalItems) }}
              </p>
            </div>
          </b-col>
        </b-row>

        <!-- Data Table -->
        <b-table
          :items="demoCars"
          :fields="fields"
          class="table-bordered table-hover col-md-12"
          no-local-sorting
          :small="true"
          :busy="isLoading"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="9"></vcl-table>
          </template>

          <!-- Product Code Cell -->
          <template v-slot:cell(productCode)="row">
            <div style="cursor: pointer">
              <span class="productCode mt-1 mr-1" @click="editItem(row.item)">
                {{ row.item.productCode }}
              </span>
              <i
                class="fas fa-copy"
                style="cursor: pointer; font-size: 11px"
                v-clipboard:copy="row.item.productCode"
                title="Copy mã SP"
                @click="makeToastSuccess('Copy mã SP')"
              ></i>
            </div>
          </template>

          <!-- Product Name Cell -->
          <template v-slot:cell(productName)="row">
            <div>
              <span
                v-text="row.item.productName"
                class="d-block white-space"
              ></span>
            </div>
          </template>

          <!-- Chassis Number Cell -->
          <template v-slot:cell(chassisNumber)="row">
            <div>
              <span v-text="row.item.chassisNumber" class="d-block"></span>
            </div>
          </template>

          <!-- License Plate Cell -->
          <template v-slot:cell(licensePlate)="row">
            <div>
              <span v-text="row.item.licensePlate" class="d-block"></span>
            </div>
          </template>

          <!-- Last Maintenance Date Cell -->
          <template v-slot:cell(lastMaintenanceDate)="row">
            <div>
              <span
                v-text="formatDate(row.item.lastMaintenanceDate)"
                class="d-block"
              ></span>
            </div>
          </template>

          <!-- Created At Cell -->
          <template v-slot:cell(createdAt)="row">
            <div>
              <span
                v-text="formatDate(row.item.createdAt)"
                class="d-block"
              ></span>
            </div>
          </template>

          <!-- Current Kilometer Cell -->
          <template v-slot:cell(currentKm)="row">
            <div style="text-align: end">
              <span v-text="row.item.currentKm"></span>
            </div>
          </template>

          <!-- Status Cell -->
          <template v-slot:cell(status)="row">
            <div class="text-center">
              <span
                v-text="getStatusText(row.item.status)"
                class="label font-weight-bold label-lg label-inline"
                v-bind:class="{
                  'label-light-success': row.item.status === 0,
                  'label-light-danger': row.item.status === 1,
                }"
                style="width: max-content"
              ></span>
            </div>
          </template>

          <!-- Actions Cell -->
          <template v-slot:cell(actions)="row">
            <div class="d-flex justify-content-center">
              <b-dropdown size="sm" id="dropdown-left" no-caret right>
                <template slot="button-content">
                  <i
                    style="font-size: 1rem; padding-right: 0px"
                    class="flaticon2-settings"
                  ></i>
                </template>
                <b-dropdown-item @click="editItem(row.item)" v-if="canUpdate">
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem" class="flaticon2-pen"></i>
                    &nbsp; Chỉnh sửa
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  @click="showDeleteConfirmation(row.item)"
                  v-if="canDelete"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #d33"
                      class="flaticon2-rubbish-bin-delete-button"
                    ></i>
                    &nbsp; Xóa
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row>
          <b-col>
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số:
              {{ convertPrice(totalItems) }}
            </p>
          </b-col>
          <b-col>
            <b-pagination-nav
              class="custom-pagination"
              v-if="totalPages >= 2"
              :link-gen="linkGen"
              :number-of-pages="totalPages"
              use-router
              @change="onPageChange"
              :value="currentPage"
              align="right"
              first-class="page-item-first btn btn-icon btn-sm m-1"
              prev-class="page-item-prev btn btn-icon btn-sm m-1"
              next-class="page-item-next btn btn-icon btn-sm m-1"
              last-class="page-item-last btn btn-icon btn-sm m-1"
              page-class="btn btn-icon btn-sm border-0 m-1"
            >
              <template v-slot:first-text>
                <span>
                  <i class="ki ki-double-arrow-back icon-xs"></i>
                </span>
              </template>

              <template v-slot:prev-text>
                <i class="ki ki-arrow-back icon-xs"></i>
              </template>

              <template v-slot:next-text>
                <i class="ki ki-arrow-next icon-xs"></i>
              </template>

              <template v-slot:last-text>
                <span class="text-info">
                  <i class="ki ki-double-arrow-next icon-xs"></i>
                </span>
              </template>
            </b-pagination-nav>
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Swal from 'sweetalert2';
import localData from '@/utils/saveDataToLocal';
import { VclTable } from 'vue-content-loading';
import { convertPrice, makeToastSuccess, makeToastFaile } from '@/utils/common';
import { createFile, getFileName } from '@/utils/file-download';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { DemoCarsService } from '../../../core/services/api/demo-cars';

import {
  getStartOfDay,
  getEndOfDay,
  DP_CONFIG,
  getSubtractMonths,
  getCurrentDate,
  checkIsAfterTime,
  DATETIME_DB_FORMAT,
} from '../../../utils/date';

import {
  DEMO_TICKET_STATUS,
  getTicketStatus,
} from '../../../utils/types/demo-ticket';
import { PERMISSIONS } from '../../../utils/enum';

export default {
  components: {
    KTCodePreview,
    VclTable,
  },

  data() {
    return {
      // Table fields
      fields: [
        {
          key: 'productCode',
          label: 'Mã sản phẩm',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'chassisNumber',
          label: 'Số khung',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'licensePlate',
          label: 'Biển số',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'lastMaintenanceDate',
          label: 'Ngày bảo dưỡng gần nhất',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'currentKm',
          label: 'Số km hiện tại',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
        },
      ],

      // Constants
      LIMIT_PAGE: 10,
      THREE_MONTHS_AGO: getSubtractMonths(3),
      NOW: getCurrentDate(),

      // Filters
      filters: {
        productName: '',
        chassisNumber: '',
        licensePlate: '',
        status: '',
        fromDate: getSubtractMonths(3),
        toDate: getCurrentDate(),
      },

      // Configs
      configs: DP_CONFIG,

      // Options
      statusOptions: DEMO_TICKET_STATUS,
      storeOptions: [],
    };
  },

  computed: {
    ...mapGetters({
      demoCars: 'demoCars/getDemoCars',
      totalItems: 'demoCars/getTotalItems',
      totalPages: 'demoCars/getTotalPages',
      currentPage: 'demoCars/getCurrentPage',
      isLoading: 'demoCars/isLoading',
      error: 'demoCars/getError',
    }),

    // Calculate pagination display values
    fromNo() {
      return (this.currentPage - 1) * this.LIMIT_PAGE + 1;
    },

    toNo() {
      return Math.min(this.LIMIT_PAGE * this.currentPage, this.totalItems);
    },

    // Permission
    canView() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_VIEW);
    },

    canInsert() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_INSERT);
    },

    canUpdate() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_UPDATE);
    },

    canDelete() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_DELETE);
    },

    canExport() {
      return this.checkPermission(PERMISSIONS.DEMO_TICKET_EXPORT);
    },
  },

  mounted() {
    if (!this.canView) {
      this.$router.push({ name: 'dashboard' });
      makeToastFaile('Bạn không có quyền xem danh sách xe Demo');
      return;
    }

    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'CRM', route: '/crm' },
      { title: 'Danh sách xe Demo' },
    ]);
  },

  created() {
    this.fetchData();
  },

  methods: {
    ...mapActions({
      fetchDemoCars: 'demoCars/fetchDemoCars',
      deleteDemoCar: 'demoCars/deleteDemoCar',
    }),

    convertPrice,
    makeToastSuccess,
    makeToastFaile,

    // Permission check
    checkPermission(permission) {
      return localData.checkPermission(permission);
    },

    // Format date
    formatDate(date, format = 'DD/MM/YYYY') {
      if (!date) return '';
      return moment(date).format(format);
    },

    // Get status text
    getStatusText(status) {
      return getTicketStatus(status);
    },
    // Pagination link generator
    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`;
    },

    // Handle page change
    onPageChange(page) {
      this.fetchData(page);
    },

    // Reset filters
    resetFilters() {
      this.filters = {
        productName: '',
        chassisNumber: '',
        licensePlate: '',
        status: '',
        fromDate: getSubtractMonths(3),
        toDate: this.NOW,
      };
      this.fetchData(1);
    },

    // Apply filters
    onFilter() {
      this.fetchData(1); // Reset to page 1 when filtering
    },

    // Fetch data
    async fetchData(page = 1) {
      const pageNumber = page || this.$route.query.page;

      const isValid = this.validateDateFilter();
      if (!isValid) return;

      try {
        const params = {
          ...this.filters,
          page: pageNumber,
          fromDate: getStartOfDay(this.filters.fromDate),
          toDate: getEndOfDay(this.filters.toDate),
        };
        await this.fetchDemoCars(params);
      } catch (error) {
        makeToastFaile('Lỗi khi tải dữ liệu');
        console.error(error);
      }
    },

    // Create new demo car
    createNewDemoCar() {
      this.$router.push({ name: 'demo-cars.create' });
    },

    // Edit demo car
    editItem(item) {
      this.$router.push({
        name: 'demo-cars.edit',
        query: { id: item.id },
      });
    },

    // Show delete confirmation
    showDeleteConfirmation(item) {
      Swal.fire({
        title: 'Xóa xe Demo!',
        text: 'Bạn có chắc muốn xóa xe Demo này không?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'center',
      }).then(async (result) => {
        if (result.value) {
          try {
            const result = await this.deleteDemoCar(item.id);

            if (result.success) {
              makeToastSuccess(result.message);
              this.fetchData(this.currentPage);
            } else {
              makeToastFaile(result.message);
            }
          } catch (error) {
            makeToastFaile('Có lỗi xảy ra khi xóa');
            console.error(error);
          }
        }
      });
    },

    validateDateFilter() {
      if (this.filters.toDate && this.filters.fromDate) {
        const toDate = getEndOfDay(this.filters.toDate);
        const fromDate = getStartOfDay(this.filters.fromDate);

        if (!checkIsAfterTime(toDate, fromDate, DATETIME_DB_FORMAT)) {
          makeToastFaile('Ngày kết thúc phải sau ngày bắt đầu');
          return false;
        }
      }
      return true;
    },

    // Handle export excel with date range
    async handleExportExcel() {
      try {
        const isValid = this.validateDateFilter();
        if (!isValid) return;

        const params = {
          ...this.filters,
          fromDate: getStartOfDay(this.filters.fromDate),
          toDate: getEndOfDay(this.filters.toDate),
        };

        const response = await DemoCarsService.exportExcel(params);

        const fileName = getFileName(response) || 'danh-sach-xe-demo.xlsx';
        createFile(response.data, fileName);
        makeToastSuccess('Xuất Excel thành công');

        this.$bvModal.hide('export-excel-modal');
      } catch (error) {
        makeToastFaile('Lỗi khi xuất Excel');
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss">
.list-demo-cars {
  .labelInput {
    font-weight: 600;
  }
  .productCode:hover {
    text-decoration: underline;
  }

  .inputText {
    width: 22%;
  }
  .btn.btn-icon.btn-sm {
    height: calc(1.35em + 1.1rem + 2px);
    width: auto;
  }

  .icon:hover {
    background-color: #90c6fc;
  }

  .dropdown-header {
    color: #3699ff !important;
  }
  .table th,
  .table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
  }
  .table.b-table.table-sm
    > thead
    > tr
    > [aria-sort]:not(.b-table-sort-icon-left),
  .table.b-table.table-sm
    > tfoot
    > tr
    > [aria-sort]:not(.b-table-sort-icon-left) {
    background-position: right calc(1.5rem / 2) center;
    padding-right: calc(0.3rem + 0.65em);
  }

  .page-item.disabled {
    cursor: not-allowed;
    &.bv-d-xs-down-none {
      .page-link {
        margin: 0.35rem 0.25rem;
        text-align: center;
      }
    }
  }

  .form-group label {
    font-weight: 500;
  }

  .white-space {
    white-space: pre-wrap;
  }
}
</style>

<style scoped>
input.form-control {
  border: 1px solid #ced4da;
}

.sttProClass {
  width: 20px;
}

.nameProClass {
  width: 30%;
  padding-left: 8px;
  align-items: center;
}

.statusProClass {
  width: 12%;
}

.actionProClass {
  width: 7%;
}

.white-space {
  white-space: pre-wrap;
}

.select-style {
  height: 31px;
}
</style>
